import { Apollo } from 'src/gql/generated';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import type { IntegrationRecordMap } from 'src/hooks';

export function getLaunchTemplatePageTitle(templateName?: string | null) {
  if (!templateName) return 'Launch template';
  return `Launch "${templateName}"`;
}

export function getShouldShowDocumentsSection({
  moreDocumentIds,
  templateWorkflowFiles,
}: {
  moreDocumentIds: UUID[];
  templateWorkflowFiles: readonly unknown[];
}) {
  return Boolean(templateWorkflowFiles.length || moreDocumentIds.length);
}

export function getShouldShowDocumentFieldsSection(alineFieldCount: number) {
  return alineFieldCount > 0;
}

/**
 * Only fields of documents the launch will copy. A cached template can still list fields of a
 * document deleted from the template elsewhere, and a document removed or replaced with
 * third-party paper on the launch page never reaches the workflow, so its values would be rejected.
 */
export function getLaunchDocumentFields<Field extends { usedInDocuments: readonly UUID[] }>(
  {
    files,
    intakeFields,
  }: { files: readonly { document: { id: UUID } }[]; intakeFields: readonly Field[] },
  {
    excludedDocumentIds,
    replacementFiles,
  }: { excludedDocumentIds: readonly UUID[]; replacementFiles: readonly { templateId: UUID }[] }
): Field[] {
  const droppedIds = new Set([...excludedDocumentIds, ...replacementFiles.map(f => f.templateId)]);
  const launchedIds = new Set(files.map(f => f.document.id).filter(id => !droppedIds.has(id)));

  return intakeFields.flatMap(field => {
    const usedInDocuments = field.usedInDocuments.filter(id => launchedIds.has(id));
    return usedInDocuments.length ? [{ ...field, usedInDocuments }] : [];
  });
}

/**
 * The CRMs the template pulls fields from, plus URL preselects. A CRM the template never reads
 * stays out even when connected, so a template with no CRM fields shows no Integrations box. A
 * mapped CRM shows even when disconnected, so its empty placeholders get a warning.
 */
export function getLaunchTemplateConnectionsToShow({
  integrationRecordMap,
  mappedCrmIntegrations,
  userConnections,
}: {
  integrationRecordMap: IntegrationRecordMap;
  mappedCrmIntegrations: ReadonlySet<Apollo.Integration>;
  userConnections: Apollo.ApiDeckConnectionSimpleFragment[];
}) {
  const urlPreselectedIntegrations = Object.values(Apollo.Integration).filter(integration =>
    Boolean(integrationRecordMap[integration])
  );
  const integrationsToShow = new Set([...mappedCrmIntegrations, ...urlPreselectedIntegrations]);
  const connectionByIntegration = new Map(
    userConnections.map(connection => [connection.integration, connection])
  );

  return [...integrationsToShow].map(
    integration =>
      connectionByIntegration.get(integration) ?? createUnavailableLaunchConnection(integration)
  );
}

/**
 * Only the CRMs the template pulls fields from can leave a document empty, so an
 * unlinked CRM the template never reads is not worth warning about.
 */
export function getLaunchWarningConnections({
  connectionsToShow,
  mappedCrmIntegrations,
}: {
  connectionsToShow: Apollo.ApiDeckConnectionSimpleFragment[];
  mappedCrmIntegrations: ReadonlySet<Apollo.Integration>;
}) {
  return connectionsToShow.filter(connection => mappedCrmIntegrations.has(connection.integration));
}

function createUnavailableLaunchConnection(
  integration: Apollo.Integration
): Apollo.ApiDeckConnectionSimpleFragment {
  return {
    __typename: 'ApiDeckConnection',
    credentialsMode: null,
    enabled: false,
    icon: '',
    id: integration,
    integration,
    integrationState: null,
    name: INTEGRATION_DISPLAY_NAME[integration],
    state: Apollo.ApiDeckConnectionState.Available,
    tagLine: null,
  };
}
